import * as THREE from 'three';
import { Layer } from '../RenderLayers';

/* ═══════════════════════════════════════════════════════════════════════════
   Le TROU NOIR — la mise en scène BESPOKE de la charge d'ulti du CANON ORBITAL
   (armes laser). Pendant la charge, l'énergie s'amasse dans un vortex : un cœur
   sombre entouré d'un disque d'accrétion qui tournoie et se resserre, de plus en
   plus vif à mesure que la charge monte, avant la décharge du faisceau.

   Choix technique : un QUAD à shader dans la scène, pas une distorsion écran.
   Une vraie lentille gravitationnelle demanderait un effet de post dédié ; ici,
   un disque d'accrétion procédural (anneau lumineux qui spirale autour d'un vide
   sombre) lit clairement « trou noir » pour un coût trivial et zéro dépendance,
   et il BLOOME comme le reste de la scène. Additif : le cœur reste noir parce
   qu'on n'y AJOUTE rien (alpha nul au centre), l'anneau brille parce qu'on y
   ajoute — exactement la silhouette voulue.
   ═══════════════════════════════════════════════════════════════════════════ */

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform float uTime;
uniform float uCharge; // 0 → 1, l'intensité qui monte pendant la charge

void main() {
  vec2 p = (vUv - 0.5) * 2.0; // -1..1
  float r = length(p);
  float ang = atan(p.y, p.x);

  // Anneau d'accrétion : rien au cœur (le vide), vif à mi-rayon, éteint au bord.
  float core = smoothstep(0.16, 0.30, r);
  float ring = smoothstep(0.20, 0.42, r) * (1.0 - smoothstep(0.72, 1.0, r));

  // Bras en SPIRALE qui tournent et s'enroulent vers le centre : le mouvement
  // d'aspiration. Deux couches à vitesses différentes pour la turbulence.
  float s1 = sin(ang * 3.0 - uTime * 7.0 + r * 14.0);
  float s2 = sin(ang * 5.0 - uTime * 4.0 + r * 9.0);
  float arms = 0.55 + 0.30 * s1 + 0.15 * s2;

  float glow = ring * core * max(arms, 0.0);

  // Énergie froide (cyan → blanc) — la palette « puissance » du vaisseau, hors
  // du rouge/rose du danger (§4.5).
  vec3 col = mix(vec3(0.30, 0.65, 1.0), vec3(0.85, 0.95, 1.0), max(arms, 0.0));

  // L'éclat monte avec la charge, et un halo interne pulse juste au bord du vide.
  float rim = (1.0 - smoothstep(0.28, 0.40, r)) * core;
  float a = (glow + rim * 0.6) * uCharge;
  gl_FragColor = vec4(col * (0.7 + max(arms, 0.0) * 0.8) * uCharge, a);
}
`;

export class BlackHoleFx {
  readonly mesh: THREE.Mesh;
  private readonly uniforms: { uTime: { value: number }; uCharge: { value: number } };

  constructor() {
    this.uniforms = { uTime: { value: 0 }, uCharge: { value: 0 } };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    // Catalogue de matériaux fermé (§4.8).
    mat.customProgramCacheKey = (): string => 'black-hole-v1';

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this.mesh.frustumCulled = false;
    // Au-dessus du vaisseau : le vortex l'enveloppe pendant la charge.
    this.mesh.renderOrder = Layer.Player + 2;
    this.mesh.visible = false;
  }

  /** Affiche le vortex à une position MONDE, à une taille et une intensité de
   *  charge données. `charge` ∈ [0,1] : le disque monte en puissance. */
  show(x: number, y: number, size: number, charge: number, now: number): void {
    this.mesh.visible = true;
    this.mesh.position.set(x, y, 0);
    this.mesh.scale.setScalar(size);
    this.uniforms.uCharge.value = charge;
    this.uniforms.uTime.value = now;
  }

  hide(): void {
    this.mesh.visible = false;
    this.uniforms.uCharge.value = 0;
  }
}
